import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useTimer, type UseTimerReturn } from "@/v2/hooks/useTimer";

interface TimerContextType extends UseTimerReturn {
	/** Duration (seconds) pending entry creation — set automatically when timer stops */
	pendingDuration: number | null;
	/** Open the add-entry flow with the stopped timer's duration (for manual re-trigger) */
	requestAddEntry: () => void;
	/** Clear pendingDuration without dismissing the timer */
	clearPendingDuration: () => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export function TimerProvider({ children }: { children: ReactNode }) {
	const [pendingDuration, setPendingDuration] = useState<number | null>(null);

	const timer = useTimer({
		onStop: (elapsed) => setPendingDuration(elapsed),
	});

	const requestAddEntry = useCallback(() => {
		if (timer.phase === "stopped" && timer.finalSeconds > 0) {
			setPendingDuration(timer.finalSeconds);
		}
	}, [timer.phase, timer.finalSeconds]);

	const clearPendingDuration = useCallback(() => {
		setPendingDuration(null);
	}, []);

	return (
		<TimerContext.Provider value={{ ...timer, pendingDuration, requestAddEntry, clearPendingDuration }}>
			{children}
		</TimerContext.Provider>
	);
}

export function useTimerContext() {
	const context = useContext(TimerContext);
	if (!context) throw new Error("useTimerContext must be used within TimerProvider");
	return context;
}
